import React, { useCallback, useEffect, useState } from "react";

import { ShieldCheck } from "lucide-react";

import { getAPIPayload } from "../../apis/getApi";
import { postAPIPayload } from "../../apis/postApi";
import { Button } from "../common/customDesignedComponents/button";
import { DANIELS_LAW_DISPLAY_NAME } from "../helpers/constants";

const JESSE_REDACT_ENABLED =
  String(process.env.REACT_APP_JESSE_REDACT_ENABLED || "").toLowerCase() ===
  "true";

/**
 * Blocks protected-party dashboard access until redaction consent is recorded.
 * After approval, pending redacted packages are shared with the requesting servicer.
 */
const JesseRedactionConsentModal = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!JESSE_REDACT_ENABLED || !localStorage.getItem("authToken")) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await getAPIPayload({
        url: "jesse/redaction-consent/status",
        token: true,
      });
      if (response?.success && response?.data?.requires_consent) {
        setPayload(response.data);
        setOpen(true);
      } else {
        setOpen(false);
        setPayload(null);
      }
    } catch {
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const handleApprove = async () => {
    if (!acknowledged) {
      setError("Please confirm you understand the redaction and sharing terms.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const response = await postAPIPayload({
        url: "jesse/redaction-consent",
        data: { acknowledged: true },
        token: true,
      });
      if (!response?.success) {
        setError(response?.message || "Unable to record your approval.");
        return;
      }
      setOpen(false);
      setPayload(null);
    } catch (e) {
      setError(e?.message || "Unable to record your approval.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!JESSE_REDACT_ENABLED || loading || !open || !payload) {
    return null;
  }

  const lawName = payload.law_display_name || DANIELS_LAW_DISPLAY_NAME;
  const pending = Array.isArray(payload.pending_transactions)
    ? payload.pending_transactions
    : [];

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 p-4">
      <div
        className="w-full max-w-lg rounded-xl border border-border bg-card p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="jesse-consent-title"
      >
        <div className="mb-4 flex items-center gap-2 text-secondary">
          <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          <span className="text-sm font-semibold uppercase tracking-wide">
            {lawName} protected recording
          </span>
        </div>

        <h2
          id="jesse-consent-title"
          className="mb-3 text-xl font-semibold text-foreground"
        >
          Redacted documents require your approval
        </h2>

        <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
          {payload.consent_message ||
            `Recording professionals may prepare redacted versions of documents filed against your property under ${lawName}. Your personal information is removed from the redacted copy. The full document is only shared if you give separate express consent later.`}
        </p>

        {pending.length > 0 ? (
          <div className="mb-4 rounded-lg border border-border bg-muted/20 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Waiting for your approval ({pending.length})
            </p>
            <ul className="space-y-2 text-sm">
              {pending.map((item) => (
                <li key={item.id} className="rounded border border-border bg-background px-3 py-2">
                  <p className="font-medium text-foreground">
                    {item.instrument_type || "Recording package"}
                  </p>
                  {item.initiated_by_name ? (
                    <p className="text-muted-foreground">
                      Requested by {item.initiated_by_name}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <label className="mb-4 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <span>
            I understand that redacted recording packages will be shared with the
            requesting professional only after I approve, and that unredacted
            documents require my separate express consent.
          </span>
        </label>

        {error ? (
          <p className="mb-3 text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <Button
          type="button"
          className="w-full"
          disabled={submitting || !acknowledged}
          onClick={() => void handleApprove()}
        >
          {submitting ? "Recording approval…" : "Approve and share redacted package"}
        </Button>
      </div>
    </div>
  );
};

export default JesseRedactionConsentModal;
